/* eslint-disable @next/next/no-img-element */
import useCommon from '@utils/hooks/common';
import {Form, Spin, Statistic, notification, Skeleton as AntSkeleton} from 'antd';
import CustomInput from '@components/CustomInput';
import {useState} from 'react';
import useRequest from '@utils/hooks/useRequestCustom';
import authApi from '@utils/api/auth.api';
import {CopyOutlined, LoadingOutlined} from '@ant-design/icons';
import CustomSelect from '@components/CustomSelect';
import {QRCode} from 'react-qrcode-logo';
import styles from './styles';
import {env} from 'next-runtime-env';
import getTokenSymbol from '../getTokenSymbol';
import {useRouter} from 'next/router';
import {useTonConnectModal, useTonWallet} from '@tonconnect/ui-react';
import {isTelegramMiniApp} from '@components/layout';
import {copyTextToClipboard} from '@utils/index';

//* if amount is --, improper contractAddress may be a reason

interface Network {
  network: string;
  token: string;
  fee: string;
  decimalUnit: number;
  contractAddress: `0x${string}`;
  networkTokenId: any;
}

interface BalanceInfo {
  decimals: number;
  formatted: string;
  symbol: string;
  value: bigint;
}

interface Balances {
  [key: string]: BalanceInfo;
}

const MOCK_NETWORKS = [
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 18,
    contractAddress: '0x55d398326f99059ff775485246999027b3197955' as `0x${string}`,
    networkTokenId: 'USDT_BSC',
  },
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 6,
    contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7' as `0x${string}`,
    networkTokenId: 'USDT_ETH',
  },
  {
    network: 'TRC20',
    token: 'USDT',
    fee: '0',
    decimalUnit: 6,
    contractAddress: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t' as any,
    networkTokenId: 'USDT_TRX',
  },
];

export const DEFAULT_NETWORK_TOKEN_ID = 'USDT_ETH';
const TON_DEFAULT_TOKEN = 'USDT_TON';

const FORM_INITIAL_VALUES = {blockchain: 'usdt'};

const EmailContent = () => {
  const {classes, t, user, system} = useCommon({
    withTranslation: ['deposit_withdraw'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  const router = useRouter();
  const isDebug = router && router.query.isDebug === 'true';

  const [form] = Form.useForm();

  const [networks, setNetworks] = useState<Network[]>([]);
  const [selectedToken, setSelectedToken] = useState<
    'USDT_ETH' | 'USDT_BSC' | 'USDT_TRX' | 'USDT_TON'
  >('USDT_ETH');
  const [regType, setRegType] = useState<'WALLET' | 'METAMASK' | 'EMAIL'>(null);
  const [clickCount, setClickCount] = useState(0);

  const wallet = useTonWallet();
  const {state, open, close} = useTonConnectModal();

  const {
    data,
    loading: loadingDepositAddress,
    runAsync: getDepositAddress,
  } = useRequest(authApi.depositAddress, {
    onSuccess: result => {
      form.setFieldValue('depositAddress', result?.data?.walletAddress);
    },
  });

  const {data: depositAddress} = data || {};
  const {memo, walletAddress} = depositAddress || {};

  // TODO @@ to true for dev
  const isTg = isTelegramMiniApp;
  // const isTg = true;

  const {data: depositInfo, loading: loadingDepositInfo} = useRequest(authApi.getDepositInfo, {
    onSuccess: result => {
      // console.log('@@ result?.networks', result?.networks);
      if (isDebug) {
        setNetworks(MOCK_NETWORKS);
      } else {
        setNetworks(result?.networks);
      }
      setRegType(result?.registrationType);
      if (isTg) {
        form.setFieldValue('network', TON_DEFAULT_TOKEN);
      } else {
        form.setFieldValue('network', DEFAULT_NETWORK_TOKEN_ID);
      }
    },
  });

  const handleClick = () => {
    setClickCount(prevCount => prevCount + 1);
    if (clickCount === 5) {
      notification.success({
        message: 'debug mode on',
      });
      system?.setIsDebugMode(true);
    }
  };

  const handleCopyClick = async () => {
    if (!form.getFieldValue('depositAddress')) return;

    try {
      await copyTextToClipboard(form.getFieldValue('depositAddress'));
      notification.success({message: t('common:copy_success')});
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  const renderQrCodeSection = () => {
    return (
      <section className={classes.qrCodeArea} onClick={handleClick}>
        {selectedToken === 'USDT_TON' && memo && (
          <div style={{marginBottom: 10, color: 'red', textAlign: 'center'}}>
            <div>{t('memo_reminder_1', {memo})}</div>
          </div>
        )}
        {depositAddress?.walletAddress ? (
          <QRCode
            style={{width: 135, height: 135}}
            value={depositAddress?.walletAddress}
            logoImage="/icon/deposit-withdraw/usdt.svg"
            removeQrCodeBehindLogo
            logoPaddingStyle="circle"
            logoPadding={2}
          />
        ) : (
          <div className={classes.spinContainer}>
            <Spin size="large" indicator={<LoadingOutlined style={{color: 'white'}} />} />
          </div>
        )}
        <div className={classes.exchangeRateContainer}>
          <span>{`${t('exchange_rate')} (USDT/USD): `}</span>
          <span className={classes.exchangeRate}>{`${depositInfo?.usdtRate || '--'}`}</span>
        </div>
        <div className={classes.depositDescription}>
          <p className={classes.formItemLabel}>{t('deposit_process_message_1')}</p>
        </div>
      </section>
    );
  };

  if (loadingDepositInfo || !regType) return <AntSkeleton paragraph={{rows: 5}} active />;

  return (
    <section className={classes.centeredContainer}>
      <Form
        className={classes.formContainer}
        layout="vertical"
        initialValues={FORM_INITIAL_VALUES}
        form={form}
        onValuesChange={async (changedValues, values) => {
          // console.log('values', changedValues, values);
          const {blockchain, network} = values;
          if (blockchain && network) {
            // setDepositAddress();
          } else {
            form.setFieldValue('depositAddress', '');
            // setQrValue(null);
          }
        }}
        name="deposit"
        scrollToFirstError
        requiredMark={false}
        onFinish={async values => {
          const depositAmountNumber = Number(values.depositAmount);
          const {depositAddress, depositAmount, network} = values;
          console.log(
            ' onFinish - depositAmountNumber, depositAddress, memo:',
            depositAmountNumber,
            depositAddress,
            memo
          );
        }}
      >
        {/* currency type */}
        <Form.Item
          name="blockchain"
          label={<span className={classes.formItemLabel}>{t('currency')}</span>}
          rules={[
            {
              required: true,
              message: t('blockchain_required'),
            },
          ]}
        >
          <CustomSelect
            className={classes.customSelect}
            options={[
              {
                label: (
                  <div className={classes.option}>
                    <div className={classes.coinArea}>
                      <img
                        src="/icon/cryptocurrency/usdt.svg"
                        alt="USD"
                        className={classes.coinIcon}
                      />
                      {t('currency_usdt')}
                    </div>
                    <div className={classes.optionBalance}>
                      <Statistic
                        valueStyle={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                        value={user?.getCoinWalletBalance()}
                      />
                    </div>
                  </div>
                ),
                value: 'usdt',
              },
            ]}
          />
        </Form.Item>

        {/* network */}
        <Form.Item
          name="network"
          label={<span className={classes.formItemLabel}>{t('network')}</span>}
          rules={[
            {
              required: true,
            },
          ]}
        >
          {/* <CustomSelect className={classes.customSelect} options={NETWORK_OPTION} /> */}
          <CustomSelect
            className={classes.customSelect}
            // options={NETWORK_OPTION}
            options={networks?.map(item => ({
              label: <span>{`${item?.network} - ${getTokenSymbol(item?.networkTokenId)}`}</span>,
              value: item?.networkTokenId,
            }))}
            onChange={async value => {
              const selectedNetwork = networks?.find(item => item?.networkTokenId === value);
              setSelectedToken(selectedNetwork?.networkTokenId);

              if (!user?.isThirdPartyGateway()) {
                const result = await getDepositAddress({networkTokenId: value});
                console.log(`@@ getDepositAddress - ${value}`, result?.data);
              }
              // console.log('selectedNetwork?.networkTokenId', selectedNetwork?.networkTokenId);
              // switchChain({chainId: selectedNetwork?.networkTokenId === 'USDT_BSC' ? 56 : 1});
            }}
          />
        </Form.Item>

        {/* exchange rate section  */}

        {/* deposit amount / deposit address  */}

        <Spin spinning={loadingDepositAddress}>
          <Form.Item
            name="depositAddress"
            label={<span className={classes.formItemLabel}>{t('deposit_address')}</span>}
          >
            <CustomInput
              disabled
              suffix={<CopyOutlined onClick={handleCopyClick} className={classes.copyIcon} />}
            />
          </Form.Item>

          {renderQrCodeSection()}
        </Spin>
      </Form>
    </section>
  );
};

export default EmailContent;
